import { useEffect, useRef } from "react";
import { Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Link2, Quote, Eraser } from "lucide-react";

/**
 * Lightweight contentEditable rich text editor.
 * Stores HTML in `value`, calls `onChange(html)` whenever content changes.
 */
const RichTextEditor = ({ value, onChange, placeholder = "Write your post...", minHeight = 240, testId = "rich-text-editor" }) => {
  const editorRef = useRef(null);
  const lastValueRef = useRef("");

  // Sync external `value` -> editor only when it differs (avoids caret reset on each keystroke).
  useEffect(() => {
    if (!editorRef.current) return;
    if (value !== lastValueRef.current) {
      editorRef.current.innerHTML = value || "";
      lastValueRef.current = value || "";
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    handleInput();
    editorRef.current?.focus();
  };

  const handleInput = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    lastValueRef.current = html;
    onChange?.(html);
  };

  const insertLink = () => {
    const url = window.prompt("Paste URL (https://...)");
    if (!url) return;
    exec("createLink", url);
  };

  const Btn = ({ onClick, title, children, testId: t }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      data-testid={t}
      className="w-8 h-8 flex items-center justify-center border-2 border-[#0A0A0A] bg-white hover:bg-[#FF2D87] hover:text-white transition-colors"
    >
      {children}
    </button>
  );

  return (
    <div className="rich-text-editor" data-testid={testId}>
      <div className="flex flex-wrap gap-1 p-2 border-2 border-b-0 border-[#0A0A0A] bg-[#FAFAFA]">
        <Btn onClick={() => exec("bold")} title="Bold" testId="rte-bold"><Bold className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("italic")} title="Italic" testId="rte-italic"><Italic className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("underline")} title="Underline" testId="rte-underline"><Underline className="w-4 h-4" /></Btn>
        <span className="w-px bg-[#0A0A0A]/30 mx-1" />
        <Btn onClick={() => exec("formatBlock", "<H2>")} title="Heading 2" testId="rte-h2"><Heading2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("formatBlock", "<H3>")} title="Heading 3" testId="rte-h3"><Heading3 className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("formatBlock", "<BLOCKQUOTE>")} title="Quote" testId="rte-quote"><Quote className="w-4 h-4" /></Btn>
        <span className="w-px bg-[#0A0A0A]/30 mx-1" />
        <Btn onClick={() => exec("insertUnorderedList")} title="Bullet list" testId="rte-ul"><List className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("insertOrderedList")} title="Numbered list" testId="rte-ol"><ListOrdered className="w-4 h-4" /></Btn>
        <span className="w-px bg-[#0A0A0A]/30 mx-1" />
        <Btn onClick={insertLink} title="Insert link" testId="rte-link"><Link2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("removeFormat")} title="Clear formatting" testId="rte-clear"><Eraser className="w-4 h-4" /></Btn>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        data-placeholder={placeholder}
        data-testid={`${testId}-content`}
        className="rte-content w-full p-4 border-2 border-[#0A0A0A] bg-white focus:outline-none focus:border-[#FF2D87] prose max-w-none"
        style={{ minHeight: `${minHeight}px` }}
      />
    </div>
  );
};

export default RichTextEditor;
